import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableRow, Paper, TextField, Typography,
  Tooltip,
  withStyles
} from '@material-ui/core';
import { countOrACVFormatter } from '../../util/customFunctions';

    const HtmlTooltip = withStyles((theme) => ({
      tooltip: {
        backgroundColor: "rgb(255,255,255,0.8)",
        color: "black",
        fontFamily: "roboto",
        fontSize: theme.typography.pxToRem(14),
        borderRadius: "1rem",
        padding: "0px",
        boxShadow: "0 0.1rem 0.1rem 0",
        marginTop: "0.4rem",
      },
    }))(Tooltip);



const EditableGoalTable = ({ projectedMidpoint = 2490000, initialGoal = 2750000, onGoalChange }) => {
  // This 'goal' state will be the "committed" goal, shown when not editing.
  const [goal, setGoal] = useState(initialGoal);

  // This new state will hold the value currently being typed into the TextField.
  const [localEditingGoal, setLocalEditingGoal] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Initialize localEditingGoal when entering edit mode, or if initialGoal changes externally.
  useEffect(() => {
    if (isEditing) {
      // Ensure localEditingGoal is a string representation of the current goal
      setLocalEditingGoal(goal === null ? '' : String(goal));
    }
  }, [isEditing, goal]); // Depend on isEditing to re-initialize when edit mode starts, and goal in case it changes externally while not editing

  // Remove the useEffect that sends 'goal' to parent on every change.
  // The parent will now only receive updates via onGoalChange in handleKeyDown.

  const gapToFill = Math.max(goal - projectedMidpoint, 0);

  const handleLocalGoalChange = (e) => {
    const val = e.target.value;
    if (val === '') {
      setLocalEditingGoal('');
      return;
    }
    // Allow only digits
    if (/^\d*$/.test(val)) {
      setLocalEditingGoal(val);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Parse the local editing value
      const parsedGoal = localEditingGoal === '' ? null : parseInt(localEditingGoal, 10);

      // 1. Update the component's committed 'goal' state
      setGoal(parsedGoal);

      // 2. Send the parsed value to the parent
      if (onGoalChange) { // Only call if the prop is provided
        onGoalChange(parsedGoal);
      }

      // 3. Exit edit mode
      setIsEditing(false);
    }
  };

  const handleBlur = () => {
    // When the TextField loses focus (blurs):
    // Revert the local editing state to the last committed goal value,
    // and exit edit mode. This discards uncommitted changes.
    setIsEditing(false);
    // Optionally, if you want to save on blur, you would call onGoalChange here
    // For now, we're explicitly discarding changes on blur.
  };

  

  return (
    <TableContainer style={{ width: 400, marginTop: '1rem' }}>
      <Table size="small">
        <TableBody>
          <TableRow>
            <TableCell style={{ borderBottom: 'none', fontSize: '0.85rem' }}><Typography>Projected Midpoint:</Typography></TableCell>
            <TableCell style={{ borderBottom: 'none' }}></TableCell>
            <TableCell style={{ borderBottom: 'none' }}></TableCell>
            <TableCell align="right" style={{ borderBottom: 'none', fontSize: '0.85rem' }}><strong>{countOrACVFormatter(projectedMidpoint, true)}</strong></TableCell> {/* Assuming 'true' for formatter */}
          </TableRow>

          <TableRow>
            <TableCell style={{ borderBottom: 'none', fontSize: '0.85rem' }}><Typography>Goal:</Typography></TableCell>
            <HtmlTooltip title={
                                <div
                                  style={{
                                    borderTop: "solid #fee599ff",
                                    borderRadius: "0.5rem",
                                    padding: "0px",
                                    margin: 0,
                                    borderWidth: "0.25rem",
                                  }}
                                >
                                  <div className="container">
                                    <Typography
                                      style={{ padding: "0.1rem" }}
                                      className="heading-style"
                                      align="left"
                                    >{`Double-click on the cell you'd like to edit`}</Typography>
                                  </div>
                                </div>
                              }>
            <TableCell style={{ background: '#fee599', borderBottom: 'none', paddingLeft: '6px', fontSize: '0.85rem', border: '1px solid lightgrey' }}><Typography>Edit</Typography></TableCell>
            </HtmlTooltip>
            <TableCell style={{ borderBottom: 'none' }}></TableCell>

            <TableCell
              style={{
                border: '2px solid #000',
                cursor: 'pointer',
                fontSize: '0.85rem',
                width: 120,
                textAlign: 'right',
                padding: '6px 16px',
                boxSizing: 'border-box',
              }}
              align="right"
              onDoubleClick={() => setIsEditing(true)}
            >
              {isEditing ? (
                <TextField
                  value={localEditingGoal} // Use the local editing state
                  onChange={handleLocalGoalChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  autoFocus
                  type="number" // Keep type="number" as you intend for numeric input
                  inputProps={{
                    min: 0,
                    step: 1,
                    style: {
                      textAlign: 'right',
                      fontSize: '0.85rem',
                      padding: 0,
                    },
                  }}
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      fontSize: '0.85rem',
                      padding: 0,
                    },
                  }}
                  style={{
                    width: '100%',
                    fontSize: '0.85rem',
                    textAlign: 'right',
                    padding: 0,
                    background: 'transparent',
                  }}
                />
              ) : (
                // Display the committed 'goal' state here
                <strong>{countOrACVFormatter(goal, true)}</strong> 
              )}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell style={{ borderBottom: 'none', fontSize: '0.85rem' }}><Typography>Gap to fill:</Typography></TableCell>
            <TableCell style={{ borderBottom: 'none' }}></TableCell>
            <TableCell style={{ borderBottom: 'none' }}></TableCell>
            <TableCell align="right" style={{ borderBottom: 'none', fontSize: '0.85rem' }}>
              <strong >{countOrACVFormatter(gapToFill, true)}</strong> {/* Assuming 'true' for formatter */}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EditableGoalTable;